/**
 * Tenant → one property. The drill-in target; same content as the static
 * property-detail.html, rendered without a navigation.
 *
 * The copy-forward unit entry survives the port: address pre-fills from the
 * property, and after each add everything except the unit number stays put
 * with focus returned to it — a townhome run is edit-the-house-number.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, designationLabel, money, request, type Property, type Unit } from '../api';
import { useSession } from '../session';
import { AddressInput } from '../components/AddressInput';

const CAN_MANAGE = new Set(['compliance_lead', 'regional_manager']);

const PROGRAM_LABEL: Record<string, string> = {
  lihtc_9: 'LIHTC 9%',
  lihtc_4: 'LIHTC 4% / bond',
  iahtc: 'Illinois state credit',
  home: 'HOME',
  bond: 'Bond',
  section_8_pbra: 'Section 8 PBRA',
};

function DetailRows({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  const present = rows.filter(([, v]) => v);
  if (!present.length) {
    return (
      <tbody>
        <tr>
          <td className="dim" style={{ padding: '14px 13px' }}>
            Nothing on file yet.
          </td>
        </tr>
      </tbody>
    );
  }
  return (
    <tbody>
      {present.map(([label, value]) => (
        <tr key={label}>
          <td className="dim" style={{ width: 160 }}>
            {label}
          </td>
          <td>{value}</td>
        </tr>
      ))}
    </tbody>
  );
}

export function PropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const { me } = useSession();
  const canManage = CAN_MANAGE.has(me.user.role);
  const navigate = useNavigate();

  const [property, setProperty] = useState<Property | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [adding, setAdding] = useState(false);

  function loadUnits() {
    return request<{ units: Unit[] }>(`/properties/${id}/units`).then((d) => setUnits(d.units));
  }

  useEffect(() => {
    request<{ property: Property }>(`/properties/${id}`)
      .then((d) => {
        setProperty(d.property);
        return loadUnits();
      })
      .catch(() => navigate('/properties', { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!property) return null;

  const occupied = units.filter((u) => u.status === 'occupied').length;
  const vacant = units.filter((u) => u.status === 'vacant').length;
  const rentRoll = units.reduce((n, u) => n + (u.monthlyRentCents ?? 0), 0);

  const address = [
    property.addressLine1,
    property.addressLine2,
    [[property.city, property.state].filter(Boolean).join(', '), property.postalCode].filter(Boolean).join(' '),
  ].filter(Boolean);

  const programs = property.fundingTypes.map((f) => PROGRAM_LABEL[f] ?? f).join(', ');

  return (
    <>
      <div className="page__head">
        <div>
          <h1>{property.name}</h1>
          <p className="sub">
            {[
              [property.city, property.state].filter(Boolean).join(', '),
              property.county && `${property.county} County`,
            ]
              .filter(Boolean)
              .join(' · ') || 'No address on file yet'}
            {' · '}
            <Link to="/properties">all properties</Link>
          </p>
        </div>
      </div>

      <div className="grid grid-4" style={{ marginBottom: 14 }}>
        <div className="kpi">
          <b>{units.length}</b>
          <span>Units</span>
        </div>
        <div className="kpi">
          <b>{occupied}</b>
          <span>Occupied</span>
        </div>
        <div className="kpi">
          <b>{vacant}</b>
          <span>Vacant</span>
        </div>
        <div className="kpi">
          <b>{rentRoll ? money(rentRoll) : '—'}</b>
          <span>Monthly rent roll</span>
        </div>
      </div>

      <div className="grid grid-2" style={{ marginBottom: 14 }}>
        <div className="panel">
          <div className="panel__head">
            <h2>Profile</h2>
          </div>
          <div className="panel__body">
            <table className="tbl">
              <DetailRows
                rows={[
                  [
                    'Address',
                    address.length ? (
                      <>
                        {address.map((line, i) => (
                          <span key={i}>
                            {i > 0 && <br />}
                            {line}
                          </span>
                        ))}
                      </>
                    ) : null,
                  ],
                  ['Phone', property.phone],
                  ['Fax', property.fax],
                  ['Year built', property.yearBuilt],
                  ['Planned units', property.totalUnits || null],
                ]}
              />
            </table>
          </div>
        </div>

        <div className="panel">
          <div className="panel__head">
            <h2>Compliance</h2>
          </div>
          <div className="panel__body">
            <table className="tbl">
              <DetailRows
                rows={[
                  ['Programs', programs],
                  ['State project #', property.stateProjectNumber],
                  ['Monitoring agency', property.monitoringAgency],
                  ['Owner entity', property.ownerLlcName],
                  ['Owner EIN', property.ownerEin],
                ]}
              />
            </table>
          </div>
          <div className="panel__foot">
            Programs, set-asides and building BINs get their own management here when certifications arrive.
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel__head">
          <h2>Units</h2>
          <div className="spacer" />
          {canManage && (
            <button className="btn btn--sm btn--primary" onClick={() => setAdding(true)}>
              Add unit
            </button>
          )}
        </div>

        {adding && (
          <AddUnitForm
            propertyId={property.id}
            propertyAddress={property.addressLine1}
            onAdded={() => void loadUnits()}
            onCancel={() => setAdding(false)}
          />
        )}

        <div className="table-scroll">
          <table className="tbl">
            <thead>
              <tr>
                <th>Unit</th>
                <th>Address</th>
                <th>Bed</th>
                <th>Sq ft</th>
                <th>Designation</th>
                <th>Rent</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {units.length === 0 && (
                <tr>
                  <td colSpan={7} className="dim" style={{ padding: '22px 13px' }}>
                    {canManage ? 'No units yet. Add the first one above.' : 'No units yet.'}
                  </td>
                </tr>
              )}
              {units.map((u) => (
                <tr key={u.id}>
                  <td className="strong">{u.unitNumber}</td>
                  <td className="dim">{u.addressLine1 ?? '—'}</td>
                  <td>{u.bedrooms ?? '—'}</td>
                  <td>{u.squareFeet ?? '—'}</td>
                  <td>{designationLabel(u)}</td>
                  <td className="num">{money(u.monthlyRentCents)}</td>
                  <td>
                    <span className={`tag tag--${u.status === 'occupied' ? 'ok' : 'grey'}`}>{u.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function AddUnitForm({
  propertyId,
  propertyAddress,
  onAdded,
  onCancel,
}: {
  propertyId: string;
  propertyAddress: string | null;
  onAdded: () => void;
  onCancel: () => void;
}) {
  const [unitNumber, setUnitNumber] = useState('');
  const [address, setAddress] = useState(propertyAddress ?? '');
  const [bedrooms, setBedrooms] = useState('');
  const [sqft, setSqft] = useState('');
  const [designation, setDesignation] = useState('');
  const [rent, setRent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const numberRef = useRef<HTMLInputElement>(null);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const payload: Record<string, unknown> = { unit_number: unitNumber.trim() };
    if (address.trim()) payload.address_line1 = address.trim();
    if (bedrooms) payload.bedrooms = Number(bedrooms);
    if (sqft) payload.square_feet = Number(sqft);
    if (designation === 'market') payload.is_market_rate = true;
    else if (designation) payload.designation = Number(designation);
    if (rent) payload.monthly_rent_cents = Math.round(Number(rent) * 100);

    request(`/properties/${propertyId}/units`, { method: 'POST', body: payload })
      .then(() => {
        // Sticky everything but the number — the townhome-run contract.
        setUnitNumber('');
        numberRef.current?.focus();
        onAdded();
      })
      .catch((err) => {
        setError(
          err instanceof ApiError && err.code === 'unit_exists'
            ? 'That unit number already exists on this property.'
            : 'Could not add the unit.',
        );
      });
  }

  return (
    <div className="panel__body">
      <form className="form" style={{ maxWidth: 'none' }} onSubmit={onSubmit}>
        <div className="field--row">
          <div className="field">
            <label htmlFor="u-number">Unit number</label>
            <input
              id="u-number"
              ref={numberRef}
              type="text"
              required
              placeholder="101"
              autoFocus
              value={unitNumber}
              onChange={(e) => setUnitNumber(e.target.value)}
            />
          </div>
          <div className="field--row">
            <div className="field">
              <label htmlFor="u-bed">Bedrooms</label>
              <input id="u-bed" type="number" min={0} max={10} value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="u-sqft">Sq ft</label>
              <input id="u-sqft" type="number" min={1} value={sqft} onChange={(e) => setSqft(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="field">
          <label htmlFor="u-address">
            Street address <span className="hint" style={{ display: 'inline' }}>— leave as-is if the unit shares the property’s address</span>
          </label>
          <AddressInput id="u-address" value={address} onChange={setAddress} onPlace={(p) => p.line1 && setAddress(p.line1)} />
        </div>

        <div className="field--row">
          <div className="field">
            <label htmlFor="u-designation">Designation</label>
            <select id="u-designation" value={designation} onChange={(e) => setDesignation(e.target.value)}>
              <option value="">LIHTC (program limit)</option>
              {[20, 30, 40, 50, 60, 70, 80].map((n) => (
                <option key={n} value={n}>
                  {n}% AMI
                </option>
              ))}
              <option value="market">Market rate</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="u-rent">Monthly rent ($)</label>
            <input id="u-rent" type="number" min={0} step="0.01" placeholder="1125.00" value={rent} onChange={(e) => setRent(e.target.value)} />
          </div>
        </div>

        {error && <p className="login__error">{error}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn--primary" type="submit">
            Add unit
          </button>
          <button className="btn" type="button" onClick={onCancel}>
            Done
          </button>
        </div>
      </form>
    </div>
  );
}
